import { Classes as BlueprintClasses } from '@blueprintjs/core';
import { DataAnalytics, Reset } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { VoiceboxChatInput } from 'bones/components/molecules/Voicebox/shared/VoiceboxChatInput';
import { VoiceboxMarkdown } from 'bones/components/molecules/Voicebox/shared/VoiceboxMarkdown';
import { VoiceboxResponseMessage } from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Button } from 'bones/design-system/components/Button';
import { Icon } from 'bones/design-system/components/icons/Icon';
import { Body } from 'bones/design-system/components/typography/Body';
import { Heading } from 'bones/design-system/components/typography/Heading';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';

export interface VoiceboxPanelAnalysisProps {
  isOpen?: boolean;
  message: VoiceboxResponseMessage;
  settings: VoiceboxSettings;
}

export const VoiceboxPanelAnalysis: React.VFC<VoiceboxPanelAnalysisProps> = ({
  isOpen,
  message,
  settings,
}) => {
  const { copy, testIds } = useVoiceboxSchema();
  const {
    setPanelHistory,
    analysisDataByMessageId,
    requestAnalysis,
    setAnalysis,
  } = useVoiceboxPanelData();

  const messageAnalysisData = analysisDataByMessageId[message.messageId];
  const initialQuery = React.useMemo(
    () => messageAnalysisData?.key || '',
    [messageAnalysisData]
  );
  const analysisData = messageAnalysisData?.analysisDataByKey[initialQuery];

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [localQuery, setLocalQuery] = React.useState<string>('');
  const didIsOpenChange = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (didIsOpenChange && isOpen) {
      setLocalQuery('');
      inputRef.current?.focus();
    }
  }, [didIsOpenChange, isOpen, initialQuery]);

  const handleSubmit = React.useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return;
      }

      setAnalysis(message.messageId, trimmed);
      requestAnalysis(message.messageId, trimmed);
      setLocalQuery('');
    },
    [message, requestAnalysis, setAnalysis]
  );

  let result: JSX.Element | null = null;
  if (analysisData?.loading) {
    result = (
      <div
        className={BlueprintClasses.SKELETON}
        data-testid={testIds.panel.analysis.loading}
      />
    );
  } else if (analysisData?.data) {
    result = (
      <VoiceboxMarkdown
        linkTestId={testIds.panel.analysis.link}
        markdown={analysisData.data}
        messageId={message.messageId}
        settings={settings}
      />
    );
  } else if (analysisData) {
    result = (
      <div className="vb-voicebox-panel-analysis-error">
        {copy.panel.analysis.error}
        <Button
          leftIcon={Reset}
          onClick={() => {
            handleSubmit(analysisData.analysis);
          }}
          size="sm"
          testId={testIds.panel.analysis.retry}
          variant="tertiary-gray"
        >
          {copy.panel.analysis.retry}
        </Button>
      </div>
    );
  }

  return (
    <div className="vb-voicebox-panel-analysis">
      <div className="vb-voicebox-panel-analysis-header">
        <div className="vb-voicebox-panel-analysis-title">
          <Icon IconComponent={DataAnalytics} size="md" />
          <Heading size="md">{copy.panel.analysis.title}</Heading>
        </div>
        <Button
          onClick={() => {
            setPanelHistory((prev) => {
              const next = prev?.slice(0);
              const last = next?.pop();

              // istanbul ignore if
              if (!next || !last) {
                return prev;
              }

              return [...next, { ...last, isAnalysisOpen: !isOpen }];
            });
          }}
          size="xs"
          testId={
            isOpen ? testIds.panel.analysis.hide : testIds.panel.analysis.show
          }
          variant="ghost"
        >
          {isOpen ? copy.panel.analysis.hide : copy.panel.analysis.show}
        </Button>
      </div>

      <div
        className={classNames('vb-voicebox-panel-analysis-transition', {
          'vb-voicebox-panel-analysis-transition--collapsed': !isOpen,
        })}
      >
        <div className="vb-voicebox-panel-analysis-body">
          <VoiceboxChatInput
            autoFocus={false}
            inputRef={inputRef}
            inputTestId={testIds.panel.analysis.input}
            loading={analysisData?.loading}
            onChange={(evt) => {
              setLocalQuery(evt.target.value);
            }}
            onClickSend={() => {
              handleSubmit(localQuery);
            }}
            onKeyDown={(evt) => {
              if (evt.key === 'Enter') {
                evt.preventDefault();
                handleSubmit(localQuery);
              }
            }}
            placeholder={copy.panel.analysis.placeholder}
            sendTestId={testIds.panel.analysis.send}
            value={localQuery}
          />

          {analysisData?.analysis ? (
            <div className="vb-voicebox-panel-analysis-result">
              <Body size="md">{analysisData.analysis}</Body>
              {result}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
